import { useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { InsertCustomManufacturerInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { InsertCustomManufacturerInProductListingMutation } from 'api/graphQL/mutations/projectNugget.mutations'
import { processEnv } from 'components/configuration/environmentVariables'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { useCallback, useMemo, useRef } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import {
    useShallowSelector,
    useWorkspace2Actions,
    useWorkspace2Store,
} from '../../../Workspace2Store'

interface DeleteCustomManufacturerArgs {
    pillId: string
    isBOD: boolean
    elementId: string
    productFamilyId: string
    projectId: string
}

/**
 * Derives the delete-option handler for a custom manufacturer pill
 * (`ReplacementOption`'s `onDeleteOption`) and encapsulates the delete flow
 * it triggers:
 *  1. Gate behind the same beta-workspace flag as the add-side entry point
 *     (AddManufacturerButton) so delete ships with the same rollout safety
 *     as add, and only offer it for OM-published custom MFRs (not
 *     user-added ones, which aren't yet published to OM).
 *  2. On invocation, remove the pill and its BOD twin (same `pillId`, see
 *     `buildBodTwinPill`) from the tree optimistically.
 *  3. Fire the insert mutation with a blank name in the background — the
 *     server treats an empty `manufacturerName` as a delete of the pill
 *     identified by `customManufacturerProductId` (mirrors the blur-to-empty
 *     delete flow in `CustomManufacturerFields`).
 *  4. Roll back the optimistic removal if the mutation fails — the server
 *     never applied the delete, so the local tree must not silently diverge.
 */
export const useCustomManufacturerDeleteOption = (node: DataNode) => {
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const { selectedProjectElementNode } = useShallowSelector((state) => ({
        selectedProjectElementNode: state.selectedProjectElementNode,
    }))
    const { setContentItemViewNodes } = useWorkspace2Actions()

    // Apollo Client never rejects (and never surfaces `errors` on the resolved
    // result) the mutate-call promise once a hook-level `onError` is
    // configured — it assumes onError is the sole error-handling path. So the
    // rollback has to happen inside onError itself, which only receives the
    // ApolloError (no per-call context) — this ref carries the snapshot to
    // restore across from the call site to the shared onError.
    const pendingRollbackRef = useRef<Partial<
        ContentItemViewNodesType
    >[] | null>(null)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [insertCustomManufacturerInProductListing] = useMutation<
        { insertCustomManufacturerInProductListing: ContentItem[] },
        { input: Partial<InsertCustomManufacturerInput> }
    >(InsertCustomManufacturerInProductListingMutation, {
        onError: (error) => {
            if (pendingRollbackRef.current) {
                // Server never applied the delete — restore the pill (and its
                // BOD twin) instead of leaving the tree out of sync.
                setContentItemViewNodes(pendingRollbackRef.current)
                pendingRollbackRef.current = null
            }
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem deleting the manufacturer. ${error.message}`,
                    variant: 'error',
                    id:
                        'WorkspaceEditor_DeleteCustomManufacturerInProductListingError',
                },
            })
        },
    })

    const deleteCustomManufacturer = useCallback(
        ({
            pillId,
            isBOD,
            elementId,
            productFamilyId,
            projectId,
        }: DeleteCustomManufacturerArgs) => {
            if (!pillId) return

            // Read fresh from the store (not a subscribed selector) so this
            // callback's identity stays stable across renders — it only
            // depends on the primitive args passed in, not on every
            // contentItemViewNodes change elsewhere in the tree.
            const previousNodes = useWorkspace2Store.getState()
                .contentItemViewNodes
            pendingRollbackRef.current = previousNodes
            setContentItemViewNodes(
                previousNodes.filter((n) => n.pillId !== pillId)
            )

            insertCustomManufacturerInProductListing({
                variables: {
                    input: {
                        elementId,
                        isBOD,
                        manufacturerName: '',
                        productFamilyId,
                        projectId,
                        customManufacturerProductId: pillId,
                    },
                },
            }).then(() => {
                // Succeeded (onError didn't fire) — nothing to roll back.
                pendingRollbackRef.current = null
            })
        },
        [setContentItemViewNodes, insertCustomManufacturerInProductListing]
    )

    const omCustomManufacturer = useMemo(
        () =>
            !!(
                node.isCustomManufacturer &&
                (isCurrentlyOnOMWorkspace ||
                    node?.contentItemView?.isAddedInOfficeMasterDraftProject)
            ),
        [node, isCurrentlyOnOMWorkspace]
    )

    // Primitive fields (not the whole node.contentItemView/selectedProjectElementNode
    // object references) so the callback's identity only changes when a value it
    // actually uses changes — object refs churn on most renders and would
    // otherwise defeat the React.memo on ReplacementOption/Hyperlink/SelectionOption.
    const customMfrPillId = node.contentItemView?.pillId
    const customMfrIsBOD = !!node.contentItemView?.isBOD
    const customMfrProductFamilyId = node.contentItemView?.productFamilyId ?? ''
    const selectedElementId = selectedProjectElementNode?.elementId ?? ''
    // node.contentItemView.projectId is not populated on these pill nodes —
    // the selected project element is the reliable source (mirrors
    // AddManufacturerButton / CustomManufacturerFieldsWrapper).
    const selectedElementProjectId = selectedProjectElementNode?.projectId ?? ''

    const canDeleteCustomManufacturer =
        omCustomManufacturer &&
        processEnv.REACT_APP_EnableAddCustomManufacturerInBetaWorkspace === '1'

    const handleDeleteCustomManufacturer = useCallback(() => {
        if (!customMfrPillId) return
        deleteCustomManufacturer({
            pillId: customMfrPillId,
            isBOD: customMfrIsBOD,
            elementId: selectedElementId,
            productFamilyId: customMfrProductFamilyId,
            projectId: selectedElementProjectId,
        })
    }, [
        customMfrPillId,
        customMfrIsBOD,
        customMfrProductFamilyId,
        selectedElementId,
        selectedElementProjectId,
        deleteCustomManufacturer,
    ])

    return {
        onDeleteOption: canDeleteCustomManufacturer
            ? handleDeleteCustomManufacturer
            : undefined,
    }
}
